// MF-67 — Create/Edit Journal Template (Maestro-managed). Define the template's category + defaults
// (category → type, severity when the type uses it, points) and build its dynamic field schema (label, type, required, help text and
// options for choice fields), with a live preview of the entry form staff will fill.
// Advanced config (security, notification chain, communications) is Phase 2.
import { useEffect, useState } from 'react'
import { configPath } from '../paths'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import FormCard from '../components/FormCard'
import FormField from '../components/FormField'
import FormSection from '../components/FormSection'
import SeverityChips from '../components/SeverityChips'
import NumberField from '../components/NumberField'
import FieldInput, { FIELD_TYPES, HAS_OPTIONS } from '../components/FieldInput'
import ColorBadge, { Points, TypeBadge } from '../components/ColorBadge'
import { inputSx, twoColumnSx } from '../components/formStyles'
import { useSeveritiesMap, useTypes } from '../components/useLookups'
import { getCategories } from '../../../../services/journal/categories.service'
import { createTemplate, updateTemplate, getTemplate } from '../../../../services/journal/templates.service'

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const emptyField = () => ({
  id: `f_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  label: '',
  type: 'text',
  required: false,
  description: '',
  options: []
})

const iconBtn = { width: 32, height: 32, padding: 0 }

// Options editor for choice fields: type an option and press Enter, type a comma, or just leave the
// box — every way commits it. Pasting "A, B, C" (or one per line) adds all three. No duplicates.
const splitOptions = (text) =>
  String(text)
    .split(/[,\n]/)
    .map((o) => o.trim())
    .filter(Boolean)

const OptionsInput = ({ value = [], onChange, error }) => {
  const [input, setInput] = useState('')
  const commit = (list) => {
    const seen = new Set()
    onChange(
      list.filter((o) => {
        const k = o.toLowerCase()
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
    )
  }
  const labels = value.map((o) => o?.label ?? o)
  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      options={[]}
      value={labels}
      inputValue={input}
      onInputChange={(_, text, reason) => {
        if (reason === 'input' && /[,\n]/.test(text)) {
          commit([...labels, ...splitOptions(text)])
          setInput('')
        } else {
          setInput(text)
        }
      }}
      onChange={(_, opts) => {
        commit(opts.flatMap((o) => splitOptions(o)))
        setInput('')
      }}
      renderTags={(vals, getTagProps) =>
        vals.map((o, i) => {
          const { key, ...tagProps } = getTagProps({ index: i })
          return <Chip key={key} size='small' label={o} {...tagProps} />
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          error={Boolean(error)}
          placeholder={labels.length ? 'Add another option…' : 'Type an option, then Enter or comma'}
          inputProps={{ ...params.inputProps, 'aria-label': 'Options' }}
          sx={inputSx}
        />
      )}
    />
  )
}

const SubLabel = ({ children }) => (
  <span style={{ fontSize: 12, fontWeight: 600, color: 'rgb(71, 85, 105)' }}>{children}</span>
)
const ErrorText = ({ children }) =>
  children ? <span style={{ fontSize: 12, color: 'rgb(220, 38, 38)' }}>{children}</span> : null

// Problems that block saving a field (shown once the user tries to save).
const fieldProblems = (f) => ({
  label: f.label.trim() ? '' : 'Give the field a title.',
  options: HAS_OPTIONS.includes(f.type) && !(f.options || []).length ? 'Add at least one option.' : ''
})

// One row of the field builder: title, type, required, instructions (subtitle) and options.
const FieldRow = ({ field, index, total, onChange, onMove, onRemove, showErrors }) => {
  const problems = showErrors ? fieldProblems(field) : {}
  return (
    <div
      className='flex flex-col'
      style={{
        gap: 12,
        padding: 14,
        borderRadius: 10,
        border: `1px solid ${showErrors && (problems.label || problems.options) ? 'rgb(252, 165, 165)' : 'rgb(226, 232, 240)'}`,
        background: '#fff'
      }}
    >
      <div className='flex items-start flex-wrap' style={{ gap: 10 }}>
        <div className='flex flex-col' style={{ paddingTop: 18 }}>
          <IconButton
            aria-label='Move up'
            size='small'
            style={iconBtn}
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <FontAwesomeIcon icon={['fal', 'chevron-up']} style={{ fontSize: 12 }} />
          </IconButton>
          <IconButton
            aria-label='Move down'
            size='small'
            style={iconBtn}
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            <FontAwesomeIcon icon={['fal', 'chevron-down']} style={{ fontSize: 12 }} />
          </IconButton>
        </div>
        <div className='flex flex-col grow' style={{ gap: 4, minWidth: 200 }}>
          <SubLabel>Title</SubLabel>
          <TextField
            size='small'
            placeholder='e.g. Details of Incident'
            value={field.label}
            error={Boolean(problems.label)}
            onChange={(e) => onChange({ label: e.target.value })}
            inputProps={{ 'aria-label': 'Field title' }}
            sx={inputSx}
          />
          <ErrorText>{problems.label}</ErrorText>
        </div>
        <div className='flex flex-col' style={{ gap: 4 }}>
          <SubLabel>Type</SubLabel>
          <TextField
            select
            size='small'
            value={field.type}
            onChange={(e) => onChange({ type: e.target.value })}
            style={{ width: 240 }}
            SelectProps={{ SelectDisplayProps: { 'aria-label': 'Field type', 'aria-labelledby': undefined } }}
            sx={inputSx}
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t.v} value={t.v}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 13,
            color: 'rgb(51, 65, 85)',
            paddingTop: 22
          }}
        >
          <Switch
            size='small'
            checked={Boolean(field.required)}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          Required
        </label>
        <div style={{ paddingTop: 22 }}>
          <Tooltip arrow title='Remove field'>
            <IconButton aria-label='Remove field' size='small' style={iconBtn} onClick={onRemove}>
              <FontAwesomeIcon icon={['fal', 'trash-alt']} style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className='flex flex-col' style={{ gap: 12, paddingLeft: 42 }}>
        <div className='flex flex-col' style={{ gap: 4 }}>
          <SubLabel>Instructions (shown under the title)</SubLabel>
          <TextField
            size='small'
            fullWidth
            multiline
            minRows={1}
            placeholder='How to fill it, e.g. “Describe what happened in the teacher’s words.”'
            value={field.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            inputProps={{ 'aria-label': 'Instructions' }}
            sx={inputSx}
          />
        </div>
        {HAS_OPTIONS.includes(field.type) ? (
          <div className='flex flex-col' style={{ gap: 4 }}>
            <SubLabel>Options</SubLabel>
            <OptionsInput
              value={field.options || []}
              error={problems.options}
              onChange={(options) => onChange({ options })}
            />
            <ErrorText>{problems.options}</ErrorText>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const TemplateForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const severities = useSeveritiesMap()
  const { byCode: typesByCode } = useTypes()

  const [categories, setCategories] = useState([])
  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [description, setDescription] = useState('')
  // Keep every default the template already has (type, visibility…) — the form edits a subset.
  const [loadedDefaults, setLoadedDefaults] = useState({})
  // A severity code from the school's scale, or '' (none chosen). Only used when the type uses severity.
  const [severity, setSeverity] = useState('')
  const [points, setPoints] = useState('0')
  const [fields, setFields] = useState([])
  const [previewValues, setPreviewValues] = useState({})
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadingData, setLoadingData] = useState(Boolean(id))

  useEffect(() => {
    getCategories({ limit: 200 })
      .then(({ data }) => setCategories(data?.paginatedResults ?? []))
      .catch(() => setCategories([]))
  }, [])

  useEffect(() => {
    if (!id) return
    getTemplate(id)
      .then(({ data }) => {
        if (!data) return
        setTitle(data.title || '')
        setCategoryId(String(data.categoryId || ''))
        setDescription(data.description || '')
        setLoadedDefaults(data.defaults || {})
        setSeverity(data.defaults?.severity || '')
        setPoints(String(data.defaults?.points ?? 0))
        setFields(data.fields || [])
      })
      .catch(() => {})
      .finally(() => setLoadingData(false))
  }, [id])

  const pointsOk = points !== '' && points !== '-' && Number.isFinite(Number(points))
  const category = categories.find((c) => String(c._id) === String(categoryId))
  const severityRec = severities.get(severity)

  // Severity applies only when the category's type uses it (e.g. Negative); otherwise it's N/A.
  const severityApplies = Boolean(typesByCode.get(category?.type)?.usesSeverity)
  const canSave = title.trim() && categoryId && (!severityApplies || severity) && pointsOk && !saving
  const namedFields = fields.filter((f) => f.label.trim())

  const onSave = async () => {
    // Block saving while a field is incomplete (no title, or a choice field with no options).
    const invalid = fields.filter((f) => {
      const p = fieldProblems(f)
      return p.label || p.options
    })
    if (invalid.length) {
      setShowFieldErrors(true)
      enqueueSnackbar(`Complete ${invalid.length} field${invalid.length > 1 ? 's' : ''} before saving.`, {
        variant: 'warning'
      })
      return
    }
    setSaving(true)
    try {
      const payload = {
        title: title.trim(),
        description,
        categoryId,
        // Severity (or none) + points are chosen here; other defaults (visibility…) are kept as they were.
        defaults: {
          ...loadedDefaults,
          severity: severityApplies ? severity || null : null,
          points: Number(points) || 0
        },
        fields: fields.map((f, i) => ({
          ...f,
          order: i,
          options: HAS_OPTIONS.includes(f.type) ? f.options || [] : []
        }))
      }
      // The code is the stable key (analytics, seeders): set once on create, never rewritten on edit.
      if (id) await updateTemplate(id, payload)
      else await createTemplate({ ...payload, code: slug(title) })
      enqueueSnackbar('Template saved', { variant: 'success' })
      navigate(configPath('templates'))
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save template', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  const updateField = (i, patch) =>
    setFields((prev) => prev.map((f, fi) => (fi === i ? { ...f, ...patch } : f)))
  const moveField = (i, dir) =>
    setFields((prev) => {
      const next = [...prev]
      const [f] = next.splice(i, 1)
      next.splice(i + dir, 0, f)
      return next
    })

  return (
    <>
      {loadingData ? (
        <div className='flex justify-center' style={{ padding: '40px 0' }}>
          <CircularProgress />
        </div>
      ) : (
        <Box
          className='grid items-start'
          style={{ gap: 24, padding: '24px 8px', maxWidth: 1200, margin: '0 auto' }}
          sx={twoColumnSx}
        >
          <FormCard
            title={id ? 'Template details' : 'New template'}
            description='A template is a type of entry staff can record, e.g. "Listening and following instructions".'
          >
            <FormSection first>
              <FormField label='Title' required htmlFor='template-title'>
                <TextField
                  id='template-title'
                  size='small'
                  fullWidth
                  autoFocus={!id}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={inputSx}
                />
              </FormField>
              <FormField
                label='Category'
                required
                htmlFor='template-category'
                hint={
                  categories.length === 0 ? 'No categories yet — create one under Journal · Categories.' : ''
                }
              >
                <TextField
                  id='template-category'
                  select
                  size='small'
                  fullWidth
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  SelectProps={{ displayEmpty: true, labelId: 'template-category-label' }}
                  sx={inputSx}
                >
                  <MenuItem value='' disabled>
                    <em style={{ color: 'rgb(148, 163, 184)' }}>Choose a category…</em>
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormField>
              <FormField
                label='Description'
                htmlFor='template-description'
                hint='When should staff use this template?'
              >
                <TextField
                  id='template-description'
                  size='small'
                  fullWidth
                  multiline
                  minRows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={inputSx}
                />
              </FormField>
            </FormSection>

            <FormSection
              title='Level'
              description='The type comes from the category. For types graded by severity (e.g. Negative) choose how serious it is; always set the points. Entries inherit all of it and can’t overwrite it.'
            >
              <FormField label='Type' help='Set on the category — every template in it shares the same type.'>
                <div className='flex items-center' style={{ gap: 10 }}>
                  {category ? (
                    <TypeBadge code={category.type || 'neutral'} size='md' />
                  ) : (
                    <span style={{ fontSize: 13, color: 'rgb(148, 163, 184)' }}>
                      Choose a category to see it.
                    </span>
                  )}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: 'rgb(100, 116, 139)'
                    }}
                  >
                    <FontAwesomeIcon icon={['fal', 'lock']} style={{ fontSize: 11 }} />
                    from the category
                  </span>
                </div>
              </FormField>
              {severityApplies ? (
                <SeverityChips
                  value={severity}
                  onChange={(code) => setSeverity(code)}
                  label='Severity'
                  required
                  hint='How serious it is, on the school’s scale.'
                />
              ) : (
                <FormField
                  label='Severity'
                  help='Severity only applies to types that use it (e.g. Negative). Change it on the type if needed.'
                >
                  <span style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>
                    {category
                      ? 'Not applicable — this type isn’t graded by severity.'
                      : 'Choose a category first.'}
                  </span>
                </FormField>
              )}
              <FormField
                label='Points'
                required
                htmlFor='template-points'
                help='What an entry from this template is worth. Entries inherit it and can’t change it.'
                hint='Negative for demerits (e.g. -2), positive for merits (e.g. 5), 0 for notes.'
              >
                <NumberField
                  id='template-points'
                  size='small'
                  stepper
                  value={points}
                  onChange={(e) => setPoints(e.target.value)}
                  style={{ width: 160 }}
                  sx={inputSx}
                />
              </FormField>
            </FormSection>

            <FormSection
              title='Fields'
              description='Questions staff answer for this entry type, in this order.'
              action={
                <Button
                  size='small'
                  variant='outlined'
                  onClick={() => setFields((prev) => [...prev, emptyField()])}
                  startIcon={<FontAwesomeIcon icon={['fas', 'plus']} style={{ fontSize: 11 }} />}
                >
                  Add field
                </Button>
              }
            >
              {fields.length === 0 ? (
                <div
                  className='text-center'
                  style={{
                    padding: 24,
                    borderRadius: 10,
                    border: '1px dashed rgb(203, 213, 225)',
                    color: 'rgb(100, 116, 139)',
                    fontSize: 13
                  }}
                >
                  No fields yet. Entries from this template will only capture the overview.
                </div>
              ) : (
                <div className='flex flex-col' style={{ gap: 12 }}>
                  {fields.map((f, i) => (
                    <FieldRow
                      key={f.id}
                      field={f}
                      index={i}
                      total={fields.length}
                      onChange={(patch) => updateField(i, patch)}
                      onMove={(dir) => moveField(i, dir)}
                      onRemove={() => setFields((prev) => prev.filter((_, fi) => fi !== i))}
                      showErrors={showFieldErrors}
                    />
                  ))}
                </div>
              )}
            </FormSection>
          </FormCard>

          <FormCard
            title='Preview'
            description='What staff will see when they pick this template.'
            style={{ position: 'sticky', top: 16 }}
            footer={
              <>
                <Button color='inherit' onClick={() => navigate(configPath('templates'))}>
                  Cancel
                </Button>
                <Button variant='contained' disabled={!canSave} onClick={onSave}>
                  {saving ? 'Saving…' : 'Save template'}
                </Button>
              </>
            }
          >
            <div className='flex flex-col' style={{ gap: 8 }}>
              <Typography style={{ fontSize: 14, fontWeight: 600, color: 'rgb(15, 23, 42)' }}>
                {title.trim() || 'Template title'}
              </Typography>
              <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>
                {category?.name || 'No category'}
              </Typography>
              <div className='flex items-center flex-wrap' style={{ gap: 8, marginTop: 4 }}>
                <TypeBadge code={category?.type || 'neutral'} />
                {!severityApplies ? (
                  <span style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>Severity: not applicable</span>
                ) : severityRec ? (
                  <ColorBadge label={severityRec.label} color={severityRec.color} size='sm' />
                ) : (
                  <span style={{ fontSize: 12, color: 'rgb(220, 38, 38)' }}>Severity: not set</span>
                )}
                <span style={{ fontSize: 13 }}>
                  <Points value={points} /> pts
                </span>
              </div>
            </div>
            {namedFields.length ? (
              <div
                className='flex flex-col'
                style={{ gap: 16, maxHeight: '55vh', overflowY: 'auto', paddingRight: 4 }}
              >
                {namedFields.map((f) => (
                  <FieldInput
                    key={f.id}
                    field={f}
                    value={previewValues[f.id]}
                    onChange={(v) => setPreviewValues((prev) => ({ ...prev, [f.id]: v }))}
                    preview
                  />
                ))}
              </div>
            ) : (
              <Typography style={{ fontSize: 13, color: 'rgb(148, 163, 184)' }}>
                Add fields to see them here.
              </Typography>
            )}
          </FormCard>
        </Box>
      )}
    </>
  )
}

export default TemplateForm
